import React, { useState } from 'react';

type InputType = 'text' | 'multi-select' | 'single-select';

interface WizardOption {
  label: string;
  value: string;
}

export interface WizardStep {
  id: string;
  type: InputType;
  question: string;
  options?: WizardOption[];
  required?: boolean;
  validation?: {
    regex?: string;
    errorMessage?: string;
  };
  nextStep?: (input: string | string[]) => string | undefined;
}

interface WizardProps {
  steps: WizardStep[];
  onComplete: (answers: Record<string, string | string[]>) => void;
}

const DynamicWizard: React.FC<WizardProps> = ({ steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [errors, setErrors] = useState<string | null>(null);

  const step = steps[currentStep];

  const handleChange = (value: string | string[]) => {
    setAnswers(prev => ({ ...prev, [step.id]: value }));
    setErrors(null);

    if (step.type === 'single-select') {
      goToNext(value);
    }
  };

  const goToNext = (value?: string | string[]) => {
    const input = value ?? answers[step.id];

    // Validation
    if (step.required && (!input || (Array.isArray(input) && input.length === 0))) {
      setErrors('This field is required.');
      return;
    }

    if (
      step.validation?.regex &&
      typeof input === 'string' &&
      !new RegExp(step.validation.regex).test(input)
    ) {
      setErrors(step.validation.errorMessage || 'Invalid input format.');
      return;
    }

    // Decide next step
    const nextId = step.nextStep?.(input) ?? steps[currentStep + 1]?.id;
    const nextIndex = steps.findIndex(s => s.id === nextId);

    if (nextIndex === -1 || nextIndex >= steps.length) {
      onComplete(answers);
    } else {
      setCurrentStep(nextIndex);
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const isSelected = (value: string, current?: string | string[]) => {
    return Array.isArray(current) ? current.includes(value) : current === value;
  };

  if(!step) return (<></>)

  return (
    <div className="max-w-xl mx-auto p-6 border rounded shadow-md bg-white round-border quiz">
      <div className="mb-6">
        <div className="text-sm text-gray-500 mb-1 quiz-header">
          Step {currentStep + 1} of {steps.length}
        </div>
        <h2 className="text-xl font-semibold text-primary mb-2 quiz-question text-center" dangerouslySetInnerHTML={{ __html: step.question }}></h2>

        {/* Input rendering */}
        {step.type === 'text' && (
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={typeof answers[step.id] === 'string' ? answers[step.id] : ''}
            onChange={e => handleChange(e.target.value)}
          />
        )}

        {step.type === 'multi-select' && (
          <div className="space-y-2 quiz-check">
            {step.options?.map(option => (
              <label key={option.value} className="block">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={Array.isArray(answers[step.id]) && answers[step.id].includes(option.value)}
                  onChange={e => {
                    const selected = Array.isArray(answers[step.id]) ? [...answers[step.id]] : [];
                    if (e.target.checked) {
                      handleChange([...selected, option.value]);
                    } else {
                      handleChange(selected.filter(v => v !== option.value));
                    }
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        )}

        {step.type === 'single-select' && (
          <div className="space-y-2 quiz-option">
            {step.options?.map(option => (
              <button
                key={option.value}
                className={`block w-full p-2 border rounded text-left quiz-select ${
                  isSelected(option.value, answers[step.id]) ? 'bg-primary text-white' : ''
                }`}
                onClick={() => handleChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {errors && <p className="mt-2 text-sm text-red-600">{errors}</p>}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-4">
        {currentStep > 0 && (
          <button className="px-4 py-2 text-sm bg-gray-200 rounded" onClick={goBack}>
            Back
          </button>
        )}
        {step.type !== 'single-select' && (
          <button className="px-4 py-2 text-sm bg-primary text-white rounded" onClick={() => goToNext()}>
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default DynamicWizard;
